import React, { useMemo } from "react";
import { Alert, Button, Empty, Popconfirm, Space, Spin, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { TimedActionDraft, ControlOption } from "./scheduledActionTypes";
import { useScheduleSummary, describeTargets } from "./useScheduleSummary";
import { controlRequiresFloor, takesOutputNumber } from "../../utils/controlFloorRules";

const { Text, Title } = Typography;

/**
 * What a row's `position` should be called, or undefined when it means nothing
 * worth showing.
 *
 * Usually a floor, shown only when non-zero because 0 is the schema's "unused"
 * value. Two commands read it otherwise: GenericOutput's position is an OUTPUT
 * number, and calling it "Floor 12" is the confusion its own field exists to
 * end; ShuttleFloor's 0 is a real floor, so it is shown rather than hidden.
 */
function positionLabel(
  action: TimedActionDraft,
  t: (key: string, fallback: string) => string,
): string | undefined {
  const control = action.start_message_value;
  if (takesOutputNumber(control)) {
    return `${t("scheduledActions.output", "Output")} ${action.position}`;
  }
  if (action.position > 0 || controlRequiresFloor(control)) {
    return `${t("scheduledActions.floor", "Floor")} ${action.position}`;
  }

  return undefined;
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface ScheduledActionListProps {
  actions: TimedActionDraft[];
  loading: boolean;
  error?: unknown;
  onEdit?: (action: TimedActionDraft) => void;
  // Optional so callers can withhold the control by permission — an absent
  // handler hides its button (mirrors onEdit / onToggleEnabled).
  onDelete?: (action: TimedActionDraft) => void;
  onToggleEnabled?: (action: TimedActionDraft, enabled: boolean) => void;
  bankNames?: Record<string, string>;
  liftNames?: Record<string, string>;
  controlOptions?: ControlOption[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the list row's command label, unambiguous about active state.
 *
 * The start and stop commands can be the SAME Control value with opposite
 * active states — active rides on `UiCmd.active` at dispatch, not a distinct
 * message — so "CarCallLock" alone would describe both sides identically.
 * Only calls out a side's active state when it is false (the exceptional
 * case): the default true stays silent, matching this list's convention of
 * showing "target set" text only when there is something extra to say.
 */
function describeCommandPair(
  t: (key: string, fallback: string) => string,
  action: TimedActionDraft,
  controlLabelMap: Map<number, string>,
): string | undefined {
  const startLabel = controlLabelMap.get(action.start_message_value);
  if (startLabel === undefined) return undefined;

  const inactiveSuffix = ` (${t("scheduledActions.inactive", "inactive")})`;
  const start = action.start_message_active ? startLabel : `${startLabel}${inactiveSuffix}`;

  const stopLabel = controlLabelMap.get(action.stop_message_value);
  if (stopLabel === undefined) return start;

  const stop = action.stop_message_active ? stopLabel : `${stopLabel}${inactiveSuffix}`;

  return start === stop ? start : `${start} → ${stop}`;
}

function resolveError(err: unknown): string {
  if (err === undefined || err === null) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;

  if (typeof err === "object") {
    const e = err as Record<string, unknown>;

    // RTK Query FetchBaseQueryError carries the server JSON body under `data`
    // (e.g. { status, data: { status: "error", message: "..." } }).
    if (typeof e.data === "string" && e.data) return e.data;
    if (e.data && typeof e.data === "object") {
      const message = (e.data as Record<string, unknown>).message;
      if (typeof message === "string" && message) return message;
    }

    // Transport-level errors: { status: "FETCH_ERROR", error: "..." }.
    if (typeof e.error === "string" && e.error) return e.error;
    if (typeof e.message === "string" && e.message) return e.message;
    if (e.status !== undefined) return `Request failed (${String(e.status)})`;

    try {
      return JSON.stringify(err);
    } catch {
      return "Unknown error";
    }
  }

  return String(err);
}

/** Group actions by bank_id then by lift_id (undefined = bank-wide). */
function groupActions(
  actions: TimedActionDraft[],
): Map<string, Map<string | undefined, TimedActionDraft[]>> {
  return actions.reduce((byBank, action) => {
    const bankMap =
      byBank.get(action.bank_id) ??
      (() => {
        const m = new Map<string | undefined, TimedActionDraft[]>();
        byBank.set(action.bank_id, m);
        return m;
      })();
    const liftKey = action.lift_id ?? undefined;
    const group = bankMap.get(liftKey) ?? [];
    group.push(action);
    bankMap.set(liftKey, group);
    return byBank;
  }, new Map<string, Map<string | undefined, TimedActionDraft[]>>());
}

// ── Sub-component: single action row ────────────────────────────────────────

interface ActionRowProps {
  action: TimedActionDraft;
  summary: string;
  /** Target-set phrase from describeTargets; empty when there is nothing extra to say. */
  targets: string;
  controlLabel?: string;
  onEdit?: (a: TimedActionDraft) => void;
  onDelete?: (a: TimedActionDraft) => void;
  onToggleEnabled?: (a: TimedActionDraft, enabled: boolean) => void;
}

function ActionRow({
  action,
  summary,
  targets,
  controlLabel = undefined,
  onEdit = undefined,
  onDelete = undefined,
  onToggleEnabled = undefined,
}: ActionRowProps): React.ReactElement {
  const [t] = useTranslation("global");
  const position = positionLabel(action, t);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        gap: 8,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ display: "block" }}>
          {action.name}
          {position !== undefined && (
            <Text type="secondary" style={{ marginLeft: 8, fontWeight: "normal" }}>
              {position}
            </Text>
          )}
        </Text>
        {controlLabel !== undefined && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {controlLabel}
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }} ellipsis>
          {summary}
        </Text>
        {/* Where the action fires, shown only when it is more than one bank's
            worth of targets — otherwise it would restate the obvious. */}
        {targets !== "" && (
          <Text type="secondary" style={{ fontSize: 12, display: "block" }} ellipsis>
            {targets}
          </Text>
        )}
      </div>

      <Space size="small" style={{ flexShrink: 0 }}>
        {onToggleEnabled !== undefined && (
          <Switch
            size="small"
            checked={action.enabled}
            onChange={(checked) => onToggleEnabled(action, checked)}
            aria-label={t("scheduledActions.toggleEnabled", "Toggle enabled")}
          />
        )}
        {onEdit !== undefined && (
          <Button
            size="small"
            onClick={() => onEdit(action)}
            aria-label={t("scheduledActions.edit", "Edit")}
          >
            {t("scheduledActions.edit", "Edit")}
          </Button>
        )}
        {onDelete !== undefined && (
          <Popconfirm
            title={t("scheduledActions.confirmDelete", "Delete this scheduled action?")}
            onConfirm={() => onDelete(action)}
            okText={t("general.yes", "Yes")}
            cancelText={t("general.no", "No")}
          >
            <Button size="small" danger aria-label={t("scheduledActions.delete", "Delete")}>
              {t("scheduledActions.delete", "Delete")}
            </Button>
          </Popconfirm>
        )}
      </Space>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function ScheduledActionList({
  actions,
  loading,
  error = undefined,
  onEdit = undefined,
  onDelete = undefined,
  onToggleEnabled = undefined,
  bankNames = {},
  liftNames = {},
  controlOptions = [],
}: ScheduledActionListProps): React.ReactElement {
  const [t] = useTranslation("global");
  const summarize = useScheduleSummary();
  const grouped = useMemo(() => groupActions(actions), [actions]);
  const controlLabelMap = useMemo(
    () => new Map(controlOptions.map((o) => [o.value, o.label])),
    [controlOptions],
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 32 }}>
        <Spin />
      </div>
    );
  }

  if (error !== undefined) {
    return (
      <Alert
        type="error"
        message={t("scheduledActions.loadError", "Failed to load scheduled actions")}
        description={resolveError(error)}
        showIcon
      />
    );
  }

  if (actions.length === 0) {
    return <Empty description={t("scheduledActions.noActions", "No scheduled actions")} />;
  }

  return (
    <div>
      {[...grouped.entries()].map(([bankId, liftMap]) => {
        const bankLabel = bankNames[bankId] ?? bankId;
        return (
          <div key={bankId} style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              {bankLabel}
            </Title>

            {[...liftMap.entries()].map(([liftId, liftActions]) => {
              const liftLabel =
                liftId !== undefined
                  ? (liftNames[liftId] ?? liftId)
                  : t("scheduledActions.bankWide", "Bank-wide");

              return (
                <div key={liftId ?? "__bank_wide__"} style={{ marginBottom: 16 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    {liftLabel}
                  </Text>
                  {liftActions.map((action) => {
                    const controlLabel = describeCommandPair(t, action, controlLabelMap);
                    return (
                      <ActionRow
                        key={action.id ?? `${action.bank_id}-${action.name}`}
                        action={action}
                        summary={summarize(action)}
                        targets={describeTargets(action)}
                        controlLabel={controlLabel}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleEnabled={onToggleEnabled}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default ScheduledActionList;
